import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../components/hooks/useAuth';
import { getRevenueReconciliation, calculateRevenueReconciliation, updatePrimaryRevenueSource, generateTestData, getCenters } from '../../../services/supabaseService';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Card from '../../../components/ui/Card';
import Select from '../../../components/ui/Select';
import Badge from '../../../components/ui/Badge';

const RevenueReconciliation = () => {
  const { userProfile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [reconciliationData, setReconciliationData] = useState(null);
  const [selectedCenter, setSelectedCenter] = useState('');
  const [centers, setCenters] = useState([]);
  const [generatingTestData, setGeneratingTestData] = useState(false);

  useEffect(() => {
    if (userProfile?.role === 'admin') {
      fetchCenters();
    }
  }, [userProfile]);

  useEffect(() => {
    if (selectedCenter) {
      fetchReconciliation();
    }
  }, [selectedCenter]);

  const fetchCenters = async () => {
    try {
      const data = await getCenters();
      setCenters(data || []);
      if (data?.length > 0) {
        setSelectedCenter(data?.[0]?.id);
      }
    } catch (error) {
      console.error('Error fetching centers:', error);
    }
  };

  const fetchReconciliation = async () => {
    try {
      setLoading(true);
      const data = await getRevenueReconciliation(selectedCenter);
      if (data?.length > 0) {
        setReconciliationData(data?.[0]);
      } else {
        setReconciliationData(null);
      }
    } catch (error) {
      console.error('Error fetching reconciliation:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCalculate = async () => {
    try {
      setLoading(true);
      const data = await calculateRevenueReconciliation(selectedCenter);
      setReconciliationData(data);
    } catch (error) {
      console.error('Error calculating reconciliation:', error);
      alert(error?.message || 'Failed to calculate revenue reconciliation');
    } finally {
      setLoading(false);
    }
  };

  const handlePrimarySourceChange = async (source) => {
    try {
      if (!reconciliationData?.id) return;
      await updatePrimaryRevenueSource(reconciliationData?.id, source);
      setReconciliationData((prev) => ({ ...prev, primaryRevenueSource: source }));
    } catch (error) {
      console.error('Error updating primary source:', error);
      alert(error?.message || 'Failed to update primary revenue source');
    }
  };

  const handleGenerateTestData = async () => {
    if (!window.confirm('This will create sample tenants and inventory for the selected center. Continue?')) {
      return;
    }

    try {
      setGeneratingTestData(true);
      const result = await generateTestData(selectedCenter);
      alert(`Test data generated successfully!\n\nTenants: ${result?.tenants}\nInventory: ${result?.inventory}`);
      handleCalculate();
    } catch (error) {
      console.error('Error generating test data:', error);
      alert(error?.message || 'Failed to generate test data');
    } finally {
      setGeneratingTestData(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    })?.format(amount || 0);
  };

  if (userProfile?.role !== 'admin') {
    return null;
  }

  return (
    <Card className="mb-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Icon name="DollarSign" size={24} />
            Revenue Reconciliation
          </h3>
          <p className="text-sm text-gray-600 mt-1">Compare revenue calculation methods and track variance</p>
        </div>
        <div className="flex items-center gap-2">
          <Select
            value={selectedCenter}
            onChange={(e) => setSelectedCenter(e?.target?.value)}
            options={centers?.map((c) => ({ value: c?.id, label: c?.name }))}
            className="w-64"
          />
          <Button onClick={handleCalculate} loading={loading} variant="outline" className="flex items-center gap-2">
            <Icon name="RefreshCw" size={16} />
            Calculate
          </Button>
        </div>
      </div>

      {loading && !reconciliationData ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : reconciliationData ? (
        <div className="space-y-6">
          {/* Revenue Methods */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-semibold text-blue-900">Tenant Monthly Revenue</h4>
                {reconciliationData?.primaryRevenueSource === 'tenant_monthly_revenue' && (
                  <Badge variant="primary">Primary</Badge>
                )}
              </div>
              <p className="text-2xl font-bold text-blue-900">{formatCurrency(reconciliationData?.tenantMonthlyRevenue)}</p>
              <p className="text-xs text-blue-700 mt-1">Sum of all active tenant monthly rent</p>
              {reconciliationData?.primaryRevenueSource !== 'tenant_monthly_revenue' && (
                <Button
                  onClick={() => handlePrimarySourceChange('tenant_monthly_revenue')}
                  variant="outline"
                  size="sm"
                  className="mt-2 w-full"
                >
                  Set as Primary
                </Button>
              )}
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-semibold text-green-900">Inventory Computed</h4>
                {reconciliationData?.primaryRevenueSource === 'inventory_computed_revenue' && (
                  <Badge variant="success">Primary</Badge>
                )}
              </div>
              <p className="text-2xl font-bold text-green-900">{formatCurrency(reconciliationData?.inventoryComputedRevenue)}</p>
              <p className="text-xs text-green-700 mt-1">Based on pricing model: per seat, per sqft, or fixed</p>
              {reconciliationData?.primaryRevenueSource !== 'inventory_computed_revenue' && (
                <Button
                  onClick={() => handlePrimarySourceChange('inventory_computed_revenue')}
                  variant="outline"
                  size="sm"
                  className="mt-2 w-full"
                >
                  Set as Primary
                </Button>
              )}
            </div>

            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-semibold text-purple-900">Sqft Revenue</h4>
                {reconciliationData?.primaryRevenueSource === 'sqft_computed_revenue' && (
                  <Badge variant="accent">Primary</Badge>
                )}
              </div>
              <p className="text-2xl font-bold text-purple-900">{formatCurrency(reconciliationData?.sqftComputedRevenue)}</p>
              <p className="text-xs text-purple-700 mt-1">Occupied sqft × avg price per sqft</p>
              {reconciliationData?.primaryRevenueSource !== 'sqft_computed_revenue' && (
                <Button
                  onClick={() => handlePrimarySourceChange('sqft_computed_revenue')}
                  variant="outline"
                  size="sm"
                  className="mt-2 w-full"
                >
                  Set as Primary
                </Button>
              )}
            </div>
          </div>

          {/* Variance Alert */}
          {reconciliationData?.varianceFlagged && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
              <Icon name="AlertTriangle" size={20} className="text-red-500 mt-0.5" />
              <div>
                <h4 className="font-semibold text-red-900">Variance Alert</h4>
                <p className="text-sm text-red-700 mt-1">
                  Revenue variance is <strong>{reconciliationData?.variancePercent?.toFixed(2)}%</strong> (exceeds 5% threshold).
                  Please review tenant data and inventory pricing for accuracy.
                </p>
              </div>
            </div>
          )}

          {!reconciliationData?.varianceFlagged && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
              <Icon name="CheckCircle" size={20} className="text-green-500 mt-0.5" />
              <div>
                <h4 className="font-semibold text-green-900">Revenue Aligned</h4>
                <p className="text-sm text-green-700 mt-1">
                  Revenue variance is <strong>{reconciliationData?.variancePercent?.toFixed(2)}%</strong> (within acceptable range).
                </p>
              </div>
            </div>
          )}

          {/* Test Data Generator */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                  <Icon name="Database" size={20} />
                  Test Data Generator
                </h4>
                <p className="text-sm text-gray-600 mt-1">
                  Create sample tenants and inventory for the selected center to validate calculations.
                </p>
              </div>
              <Button
                onClick={handleGenerateTestData}
                loading={generatingTestData}
                variant="outline"
                className="flex items-center gap-2"
              >
                <Icon name="Zap" size={16} />
                Generate Test Data
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div className="text-center py-12">
          <Icon name="DollarSign" size={48} className="mx-auto mb-4 text-gray-400" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Reconciliation Data</h3>
          <p className="text-gray-600 mb-4">Click "Calculate" to generate revenue reconciliation for this center.</p>
          <Button onClick={handleCalculate} loading={loading}>
            Calculate Revenue
          </Button>
        </div>
      )}
    </Card>
  );
};

export default RevenueReconciliation;